import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';

import { APP_API_URL } from "../../../utils";
import { useDataContext } from "../../../context/DataContext";
import MainLayout from "../../../layout/main-layout";
import UIDropdown from "../../../components/ui/ui-dropdown";
import Loader from "../../../components/loader";

export default function AddBrokenTile() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { fetchProducts } = useDataContext(); // Assuming this fetches products if needed
  const initialFormData = {
    variant_id: '',             // Requis, ID du produit
    quantity: '',           // Requis, entier >=1
    note: '',                   // Optionnel, chaîne
    user_id: user?.id,          // Optionnel, si nécessaire
  };
  const [formData, setFormData] = useState(initialFormData);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);

  // États pour les dropdowns
  const [selectedProduct, setSelectedProduct] = useState({ label: "Sélectionner un produit", value: "" });

  useEffect(() => {
    const fetchProductsList = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${APP_API_URL}/broken-tiles/catalog`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setProducts(response.data.data || response.data); // Ajustez selon la structure de la réponse
      } catch (error) {
        console.error('Erreur lors de la récupération des produits :', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsList();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleProductSelect = (option) => {
    setSelectedProduct(option);
    setFormData(prevState => ({
      ...prevState,
      variant_id: option.value
    }));

    // Effacer l'erreur si un produit est sélectionné
    if (option.value) {
      setErrors(prevErrors => ({
        ...prevErrors,
        variant_id: undefined
      }));
    }
  };

  const validateForm = () => {
    const requiredFields = ['variant_id', 'quantity'];
    let errors = {};

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field] === "") {
        errors[field] = "Ce champ est obligatoire";
      }
    });

    if (formData.quantity && (isNaN(formData.quantity) || parseInt(formData.quantity) < 0)) {
      errors.quantity = "La quantité doit être un entier positif supérieur ou égal à 0";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/broken-tiles`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        resetForm();

        Swal.fire({
          title: 'Carreaux cassés ajoutés !',
          text: 'Les carreaux cassés ont été ajoutés avec succès.',
          icon: 'success',
        });
        fetchProducts(); // Si nécessaire pour rafraîchir la liste des produits
      }

    } catch (error) {
      if (error.response && error.response.status === 422) {
        setErrors(error.response.data.errors);
      } else if (error.response && error.response.status === 400) {
        Swal.fire({
          title: 'Erreur',
          text: error.response.data.message || 'Stock insuffisant pour ce produit.',
          icon: 'error',
        });
      } else {
        console.error('Erreur lors de l\'envoi du formulaire', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedProduct({ label: "Sélectionner un produit", value: "" });
    setErrors({});
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <MainLayout>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center mb-16'>
        <div className="pagetitle">
          <h1>Ajouter des carreaux cassés</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item active">Créer un nouvel enregistrement de carreaux cassés</li>
            </ol>
          </nav>
        </div>

        <div className='flex flex-col lg:flex-row gap-x-2 gap-y-4'>
          <div>
            <Link to="/admin/broken-tiles" className="inline-flex items-center rounded-md bg-darkgreen px-2.5 py-2 text-sm font-semibold text-white hover:bg-lightgreen transition-all duration-300">
              <i className="bi bi-arrow-left me-1" /> Retour aux carreaux cassés
            </Link>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-body pt-4">
            <div className="accordion" id="accordionPanelsStayOpenExample">
              <div className="accordion-item">
                <h2 className="accordion-header" id="flush-headingOne">
                  <button
                    className="inline-flex items-center text-lg font-semibold text-gray-900 py-3 accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#flush-collapseOne"
                    aria-expanded="true"
                    aria-controls="flush-collapseOne"
                  >
                    <div>
                      <i className="bi bi-info-circle me-1 text-lightgreen" /> Informations sur les carreaux cassés
                    </div>
                  </button>
                </h2>
                <div
                  id="flush-collapseOne"
                  className="accordion-collapse collapse show"
                  aria-labelledby="flush-headingOne"
                >
                  <div className="accordion-body">
                    <div className="row">
                      <div className="col-lg-6 col-sm-6 col-12 mb-3">
                        <label htmlFor="product" className="form-label">Produit</label>
                        <UIDropdown
                          options={products.map(product => ({
                            label: `${product.name} (${product.sku})`,
                            value: product.id
                          }))}
                          selectedOption={selectedProduct}
                          onSelect={handleProductSelect}
                          enableSearch={true}
                        />
                        {errors.variant_id && <span className="mt-[.25rem] text-[.875rem] text-[#dc3545]">{errors.variant_id}</span>}
                      </div>

                      <div className="col-lg-6 col-sm-6 col-12 mb-3">
                        <label htmlFor="quantity" className="form-label">
                          Quantité (m²)
                        </label>

                        <input
                          type="number"
                          name="quantity"
                          className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
                          value={formData.quantity}
                          onChange={handleChange}
                          min="0.1"
                          step="0.1" 
                        />

                        {errors.quantity && (
                          <span className="mt-[.25rem] text-[.875rem] text-[#dc3545]">
                            {errors.quantity}
                          </span>
                        )}
                      </div>

                      <div className="col-lg-12">
                        <div className="input-blocks summer-description-box transfer mb-3">
                          <label htmlFor="note" className="form-label">Note</label>
                          <textarea
                            className={`form-control h-100 ${errors.note ? 'is-invalid' : ''}`}
                            rows={5}
                            name="note"
                            value={formData.note}
                            onChange={handleChange}
                          />
                          {errors.note && <span className="mt-[.25rem] text-[.875rem] text-[#dc3545]">{errors.note}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row justify-end mt-4 gap-x-4">
              {isLoading ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
                >
                  <svg
                    aria-hidden="true"
                    role="status"
                    className="inline w-4 h-4 me-2 text-white animate-spin"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor"
                    />
                  </svg>
                  Enregistrement...
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
                >
                  Enregistrer
                </button>
              )}
              <button type="button" onClick={resetForm} className="inline-flex items-center rounded-md bg-gray-500 px-2.5 py-2 text-sm font-semibold text-white hover:bg-gray-800 transition-all duration-300">
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}